import React, {Fragment, useContext, useState, useEffect } from 'react';
import moment from 'moment'
import UsersContext from '../contexts/UsersContext'
import EventsContext from '../contexts/EventsContext'
import LoginContext from '../contexts/LoginContext'
import TimeDisplay from './TimeDisplay'
import Event from './Event'
import EventTools from './EventTools'
import DropdownSelector from './DropdownSelector'
import ClipLoader from "react-spinners/ClipLoader"
import Comments from './Comments'
import {totalTime} from '../timeConversion'


const monthSectionHeaderFormat = 'MMMM YYYY'
const daySectionHeaderFormat = 'dddd, Do of MMMM'

const onlyUnique = (value, index, self) => { 
  return self.indexOf(value) === index;
}

const eventsInDay = (allEvents,day) =>  allEvents.filter(event => {
  return moment(event.date).format('YYYY-MM-DD') === day}
);

const assigneesInEvents = (events) => events.reduce( (allAssignees,event) => {
  return [...allAssignees,...event.assigneeIds];
},[]).filter( onlyUnique )

const assigneeForId = (id,users) => {
  return users.find( u => parseInt(u.id) === parseInt(id) ) || {name: "Unknown"}
}

const userEvents = (allEvents,userId) => {
    const userEventsInDay = userId ? allEvents.filter(event => event.assigneeIds.includes(userId.toString())) : []
    return {numEventsInDay: allEvents.length, numUserEventsInDay: userEventsInDay.length, userEventsInDay: userEventsInDay};
}

const unassignedEvents = (events) =>  events.filter( event => Array.isArray(event.assigneeIds) ? event.assigneeIds.length === 0 : true )

const EventList = (props) => {

    const users = useContext(UsersContext)
    const login = useContext(LoginContext)
    const {events, saveEventHandler, deleteEventHandler,updateWaypointsForEvent} = useContext(EventsContext)

    const [assignee,setAssignee] = useState(login)
    const [inTransition,setIntransition] = useState({})
    const [dayInTransition,setDayInTransition] = useState({});
    const [toolboxOpenId,setToolboxOpenId] = useState(null)
    const [detailsOpenId,setDetailsOpenId] = useState(null)
    const [eventsWithMonths,setEventsWithMonths] = useState([])
    const [dayData,setDayData] = useState({})

    const assignEvents = (assignedEvents,assignStatus,callback = () =>{}) => {
      const newInTransition = {...inTransition}
      const dataSets = []

      for (const key in assignedEvents) {
        const event = assignedEvents[key];
        newInTransition[event.date] = moment().utc().format('YYYY-MM-DDTHH:mm:ss')
      }

      setTimeout(() => {
        setIntransition(newInTransition)
      }, 0);
      

      //Use a short time out, otherwise in transition indicators won't update until the first saveEventHandler returns
      setTimeout(() => {
        for (const key in assignedEvents) {
          if (assignedEvents.hasOwnProperty(key)) {
            const event = assignedEvents[key];
            const assignees = event.assigneeIds.slice()
            const eventData = event.id === 0 ? {...event} : {}
            delete eventData.id

            if(assignStatus && !assignees.includes(assignee.id.toString())){
              assignees.push(assignee.id.toString())
              dataSets.push({id: event.id, data: {...eventData, assigneeIds: assignees}})
            }else{
              dataSets.push({id: event.id, data: {...eventData, assigneeIds: assignees.filter(a => a !== assignee.id.toString())}})
            } 
          }
        }

        if( dataSets.length > 0){
          
            //Send off one first to make sure we don't create lots of new accounts on the server side.
            saveEventHandler(dataSets[0].id,dataSets[0].data)
            .then( res => {
              const promises = dataSets.slice(1).map( ds => saveEventHandler(ds.id,ds.data) )
              Promise.all(promises).then(callback)
            })
          
        }
      }, 10);

    }

    const assignEventsInDay = (day,allEvents) => {
      const newDayInTransition = {...dayInTransition}
      newDayInTransition[day] = moment().utc().format('YYYY-MM-DDTHH:mm:ss')
      setDayInTransition(newDayInTransition)
      const eventsInDay = allEvents.filter(event => moment(event.date).format(daySectionHeaderFormat) === day)
      assignEvents(eventsInDay,true, () =>{
        const doneDayInTransition = {...dayInTransition}
        delete doneDayInTransition[day];
        setDayInTransition(doneDayInTransition);

      })
    }

    useEffect( () => {
      setAssignee(login)
    }, [login])

    useEffect( () => {
        if(toolboxOpenId){
          updateWaypointsForEvent(toolboxOpenId)
        }
    }, [toolboxOpenId,updateWaypointsForEvent])

    //Insert Month and Day banners between events
    useEffect(() => {
      const eventsAndMonths = 
        events.reduce( (nextEvents,event) => {
          const month = moment(event.date).format(monthSectionHeaderFormat)
          const day = moment(event.date).format(daySectionHeaderFormat)
          
          if( nextEvents.length === 0 ){
              return [{month: month},{day: day, date: moment(event.date).format()},event]
          }else{
              const previousEvent = nextEvents[nextEvents.length-1]
              const previousMonth = moment(previousEvent.date).format(monthSectionHeaderFormat)
              const previousDay = moment(previousEvent.date).format(daySectionHeaderFormat)
              const newDay = previousDay !== day ? {day: day, date: moment(event.date).format()} : null
              const newMonth = previousMonth !== month ? {month: month} : null
              if( newMonth) nextEvents.push(newMonth);
              if( newDay ) nextEvents.push(newDay);
          }
          nextEvents.push(event)
          return nextEvents;
        },[])
    
      const dataForDay = {}
      for (const key in eventsAndMonths) {
        if (eventsAndMonths.hasOwnProperty(key)) {
          const e = eventsAndMonths[key]
          if( e.day ){
            const eventsToday = eventsInDay(events,e.date.substr(0,10))
            dataForDay[e.day] = {
              events: eventsToday,
              hasUnassignedEvents: unassignedEvents(eventsToday).length > 0,
              assignees: assigneesInEvents(eventsToday),
              userEventsInDay: assignee ? userEvents(eventsToday,assignee.id) : []
            }
          }
        }
      }
      setEventsWithMonths(eventsAndMonths)
      setDayData(dataForDay)
   
    },[events,assignee])

    if( assignee && assignee.loginUrl ){
      return <div className="alert alert-info" role="alert"><a href={assignee.loginUrl()}>Please log in first.</a></div>
    }

    if(!assignee || typeof assignee.id === 'undefined'){
      return <span>Logging in...</span>;
    }

    const assigneeEvents = events.filter( e => e.assigneeIds.includes(assignee.id.toString()) )
    let detailsVisible = false
    let dayHasUnassignedEvents = true

    const eventComponents = 
    eventsWithMonths.map( (e) => {
    
      //This is a Month banner
      if( e.month ){
          return <li className="list-group-item list-group-item-dark" key={e.month}><h4>{e.month}</h4></li>
      }
      //This is a Day banner
      if( e.day && dayData[e.day] ){
          //return <li>Test</li>;
            
          detailsVisible = detailsOpenId === e.day
          const eventsToday = dayData[e.day].events
          dayHasUnassignedEvents = dayData[e.day].hasUnassignedEvents

          const assigneesToday = dayData[e.day].assignees
          const {numEventsInDay,numUserEventsInDay} = dayData[e.day].userEventsInDay
          const dayIsInTransition = typeof dayInTransition[e.day] !== 'undefined'
          const claimText = numUserEventsInDay > 0 ? (numUserEventsInDay === numEventsInDay ? "Unclaim Day" : "Toggle Claims") : "Claim Day"
          const Spinner = () => <ClipLoader css={`display: inline-flex; vertical-align: middle; margin: 5px;`} size={20} loading={true}/> 
          const buttonContent = dayIsInTransition ? <Spinner/> : claimText

          const assigneeElements = assigneesToday.map( assigneeId => {
            const assigneeInDay = assigneeForId(assigneeId,users);
            const assigneeEvents = eventsToday.filter(event => event.assigneeIds.includes(assigneeId.toString()))
            const assigneeTotalInDay = totalTime(assigneeEvents,true)
            return (
              <li className="list-group-item">
                <div className="log-table">
                  <div><span className="badge badge-success mr-md-2" key={`assignee-${assigneeId}`}>&nbsp;{assigneeInDay.name}&nbsp;</span></div>
                  <div>
                      <TimeDisplay decimalHours={assigneeTotalInDay}/>
                  </div>
                  </div>
              </li>);
          })

          const toggleBar = <li className="list-group-toggle" onClick={() => setDetailsOpenId(detailsOpenId === e.day ? null : e.day)}><div className={`toggle-arrow arrow-${detailsOpenId === e.day ? 'up' : 'down'}`}></div></li>

          return (
            <Fragment>
              <li className="list-group-item list-group-item-info" key={e.date}>
                <div className="log-table">
                  <div className="log-cell">
                      <h6>{e.day}</h6>
                  </div>
                  <div className="log-cell">
                    <h6><TimeDisplay decimalHours={totalTime(eventsToday)}/></h6>
                  </div>
                  <div className="log-cell"></div>
                  <div className="log-cell log-claim">
                    { login.isAdmin || login.isActiveMember ? 
                      <button type="button" class="btn btn-primary" disabled={dayIsInTransition} onClick={() => assignEventsInDay(e.day,events)}>{buttonContent}</button>
                    : <Fragment/>}
                  </div>
                </div>
              </li>
              {assigneeElements}
              
              {assigneesToday.length > 0 ? <li className='list-comments'><Comments posts={eventsToday} users={users} loggedInUserId={login.id} isAdmin={login.isAdmin}/> </li> : <Fragment/>}
              
              { dayHasUnassignedEvents ? '' : toggleBar}
            </Fragment>)
      }
      //This is not a valid event
      if( !e.waypoints || !e.assigneeIds || (e.hidden && !login.isAdmin)){
          return <Fragment></Fragment>;
      }
      
      if( !detailsVisible && !dayHasUnassignedEvents ){
        return <Fragment></Fragment>;
      }

      const transition = typeof inTransition[e.date] !=='undefined' && moment(inTransition[e.date]).isAfter(e.modified_gmt)
  
      const className = e.id !== 0 ? `list-group-item has-toolbox` : "list-group-item"
      return(
        <li 
          className={`${className} ${toolboxOpenId===e.id ? 'toolbox-open' : ''}`} 
          style={{opacity: e.hidden ? 0.2 : 1, backgroundColor: "#f9f9fb", 
          background: toolboxOpenId===e.id ? "linear-gradient(to top, #cacacaff, rgb(255, 255, 255) 81.51%, #f6f6f6ff)" : "none", 
          borderLeft: e.id === 0 ? "4px solid darkgreen" : "none"}}
          onClick={e.id ? () => setToolboxOpenId(toolboxOpenId===e.id ? null : e.id) : () => {/*No-op*/}} 
          key={e.date}>
            <Event event={e} assignEvents={assignEvents} users={users} assignee={assignee} transition={transition} />
            {toolboxOpenId===e.id ? <EventTools event={e} saveEventHandler={saveEventHandler} deleteEventHandler={deleteEventHandler} enableAdminTools={login.isAdmin}/> : <Fragment/>}
        </li> )
    })

    return(
        <Fragment>
          <div class="card" style={{width: "100%"}}>
            <div className="log-table card-body">
              <div className="log-cell">Total this period:</div>
              <div className="log-cell"><TimeDisplay decimalHours={totalTime(events)}/></div>
              <div className="log-cell" style={{textAlign: "right"}}>{login.isAdmin ? <DropdownSelector options={users} selectedKey={assignee.id} disabled={false} changeHandler={setAssignee}/> : assignee.name}</div>
              <div className="log-cell" style={{textAlign: "right"}}><TimeDisplay decimalHours={totalTime(assigneeEvents,true)}/></div>
            </div>
          </div>
          <ul className="list-group list-group-flush text-left">{eventComponents}</ul>
        </Fragment>
    )
}

export default EventList